/**
 * @lines98/client/components/segmented — примитив «сегмент-переключатель» (плашка
 * взаимоисключающих кнопок с активным состоянием).
 *
 * ЗАЧЕМ (owner-directive 2026-06-04 «делаем примитивы»; модульность). Паттерн
 * «плашка из 2–4 коротких взаимоисключающих кнопок, одна активна» раньше верстался
 * на каждом экране врозь (в лидерборде — ДВАЖДЫ в одном файле: разделы + под-табы
 * Сегодня/Вчера). Это единый кирпичик: правила (тактильные 44px, один канал акцента
 * на активном, ARIA-tablist + клавиши-стрелки, размеры-токенами) зашиты ВНУТРЬ —
 * экран монтирует и наполняет, геометрию не переобъявляет. Модель «ствол → ветки».
 *
 * ГРАНИЦА применения (owner-rule «запрещён перенос текста и …»): segmented — для
 * 2–4 КОРОТКИХ лейблов, которые гарантированно влезают в ряд. Кнопки НЕ переносят
 * текст и НЕ режут «…»: если опций/длины больше, чем влезает — это НЕ segmented, а
 * `picker` (вертикальный список, полная ширина). Так перенос/обрезка невозможны by
 * design: сюда кладут только то, что заведомо помещается.
 *
 * РАЗМЕР ЛЕЙБЛОВ — group-autofit (owner-law 2026-06-12, skill §8.10-B). Подписи
 * сегментов = ОДНА ГРУППА → один ИЗМЕРЕННЫЙ размер = min, вписывающийся в самый
 * длинный лейбл. Никакого статичного font-size / padding-«подгона» под конкретное
 * слово (PROHIBITED §8.10-B) — зазор от края считается от размера сегмента, а размер
 * ТЕКСТА — измерением.
 *
 * ДВА КОНТЕКСТА (owner-law 2026-06-12 «расти до заполнения = правило ТОЛЬКО плашки»):
 *   - СЕГМЕНТ ВНЕ ПЛАШКИ (лидерборд-табы) — `selfAutofit:true` (default). Сам себя
 *     fit'ит с `capToDesign`: ТОЛЬКО УЖИМАЕТСЯ при нехватке ширины, НИКОГДА не растёт
 *     выше дизайн-токена (--fs-control) из стайлшита. Так табы лидерборда держат
 *     ПРЕЖНИЙ дизайн-размер (регрессия «выросли слишком крупно» — устранена). Размер
 *     fluid (токен масштабируется с доской), но не grow-to-fill.
 *   - СЕГМЕНТ В ПЛАШКЕ (переключатель Вход/Регистрация в auth-modal) — `selfAutofit:
 *     false`. Сегмент НЕ fit'ит себя; его лейблы включает в СВОЮ группу плашка
 *     (dialog-plaque), где правило = grow-to-fill вместе с провайдерами и CTA. Двойного
 *     autofit на одном элементе нет (плашка vs self). `.items` отдаёт кнопки плашке.
 *
 * CSS — co-located `segmented.css`.
 */

import './segmented.css';

import { attachAutofit, type AutofitHandle } from '../lib/autofit-text.js';

/**
 * Floor читаемости подписи сегмента (px). Тактильный/читаемость-минимум (skill §0.A
 * P2 / §6). 13px — owner-минимум CTA-шрифта (skill §0.A P2).
 */
const SEGMENT_FS_MIN_PX = 13;

export interface SegmentedItem {
  /** Стабильный id сегмента (возвращается в onChange). */
  readonly id: string;
  /** Короткая подпись (must-visible, не переносится — см. границу применения). */
  readonly label: string;
}

export interface SegmentedOptions {
  /** Сегменты в порядке отображения (2–4 коротких). */
  readonly items: ReadonlyArray<SegmentedItem>;
  /** Активный id (по умолчанию — первый). */
  readonly activeId?: string;
  /** Вызывается при выборе ДРУГОГО сегмента (тот же — не дёргает). */
  readonly onChange: (id: string) => void;
  /** ARIA-имя группы (role=tablist). */
  readonly ariaLabel: string;
  /** Доп. класс(ы) экрана-потребителя (позиционирование, не вид). */
  readonly className?: string;
  /**
   * Сам ли сегмент подбирает размер своих лейблов (default true). false — когда сегмент
   * живёт ВНУТРИ плашки (auth-modal): размером управляет плашка (dialog-plaque) единой
   * группой grow-to-fill, а сегмент свой self-fit НЕ запускает (иначе двойной autofit
   * на одном элементе). При false `.items` отдаёт кнопки плашке для её группы.
   * При true (вне плашки, лидерборд) — self-fit с capToDesign (shrink-only, прежний
   * дизайн-размер; owner-law 2026-06-12).
   */
  readonly selfAutofit?: boolean;
}

export interface SegmentedHandle {
  /** Корневой элемент плашки (role=tablist). */
  readonly element: HTMLElement;
  /**
   * Кнопки-табы (текст-элементы) — для плашки, когда `selfAutofit:false`: плашка
   * включает их в свою group-uniform группу. Текст лейбла = textContent самой кнопки,
   * высотная опора = она же (min-height --btn-h) → пары heightEl не нужно.
   */
  readonly items: readonly HTMLButtonElement[];
  /** Программно выставить активный сегмент (без вызова onChange). */
  setActive(id: string): void;
  /** Снять слушатели (на размонтировании экрана). */
  destroy(): void;
}

/** Создать сегмент-переключатель. Возвращает handle с `.element`. */
export function createSegmented(opts: SegmentedOptions): SegmentedHandle {
  const root = document.createElement('div');
  root.className = 'segmented';
  if (opts.className) root.classList.add(...opts.className.split(/\s+/).filter(Boolean));
  root.setAttribute('role', 'tablist');
  root.setAttribute('aria-label', opts.ariaLabel);

  let activeId = opts.activeId ?? opts.items[0]?.id ?? '';

  const buttons = new Map<string, HTMLButtonElement>();

  const applyActive = (): void => {
    for (const [id, btn] of buttons) {
      const isActive = id === activeId;
      btn.classList.toggle('segmented-item--active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
      // Roving tabindex — активный в табе, остальные доступны стрелками.
      btn.tabIndex = isActive ? 0 : -1;
    }
  };

  const select = (id: string, focus: boolean): void => {
    if (id === activeId) return;
    activeId = id;
    applyActive();
    if (focus) buttons.get(id)?.focus();
    opts.onChange(id);
  };

  const ids = opts.items.map((it) => it.id);

  for (const item of opts.items) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'segmented-item';
    btn.setAttribute('role', 'tab');
    btn.dataset.id = item.id;
    btn.textContent = item.label;

    btn.addEventListener('click', () => select(item.id, false));
    btn.addEventListener('keydown', (e) => {
      // Стрелки — переход между сегментами (roving tabindex, ARIA-tablist).
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const idx = ids.indexOf(activeId);
      const next = e.key === 'ArrowRight' ? (idx + 1) % ids.length : (idx - 1 + ids.length) % ids.length;
      select(ids[next]!, true);
    });

    buttons.set(item.id, btn);
    root.appendChild(btn);
  }

  applyActive();

  const itemEls = [...buttons.values()];

  // SELF-FIT (только вне плашки — лидерборд). Group-autofit подписей с `capToDesign`:
  // ОДИН размер на группу = min, вписывающийся в длиннейший лейбл, НО НЕ ВЫШЕ дизайн-
  // токена (--fs-control) из стайлшита. Так табы держат ПРЕЖНИЙ дизайн-размер (только
  // shrink при нехватке ширины, без grow-to-fill — owner-law 2026-06-12). mode:'line'
  // (segmented не переносит). В плашке (selfAutofit:false) self-fit ВЫКЛЮЧЕН — размером
  // правит плашка (grow-to-fill вместе с провайдерами/CTA); двойного autofit нет.
  const selfAutofit = opts.selfAutofit ?? true;
  const fit: AutofitHandle | null = selfAutofit
    ? attachAutofit(root, [
        {
          selector: '.segmented-item',
          uniform: true,
          mode: 'line',
          minPx: SEGMENT_FS_MIN_PX,
          capToDesign: true,
          // НЕ-обрезающий последний рубеж (skill §0.A P1 visibility > эстетика nowrap):
          // если на floor-шрифте длинный i18n-лейбл («Доска дня» → it «Griglia del giorno»
          // / pt «Tabuleiro diário» / th) ВСЁ РАВНО шире сегмента ОДНОЙ строкой — перенос
          // ПО СЛОВАМ (.is-clamped в segmented.css), а не crop «…»/hard-clip и не уход под
          // floor. Класс ставится ТОЛЬКО на реально переполненные сегменты (детектор по
          // one-line canvas-ширине — без осцилляции; короткие лейблы — одной строкой).
          clampClass: 'is-clamped',
        },
      ])
    : null;

  return {
    element: root,
    items: itemEls,
    setActive(id: string): void {
      if (!buttons.has(id) || id === activeId) return;
      activeId = id;
      applyActive();
    },
    destroy(): void {
      // Слушатели click/keydown висят на кнопках внутри root; удаление root из DOM
      // экраном их освобождает. Глобальные слушатели autofit (VIEWPORT_APPLIED_EVENT
      // на window) снимаем явно — иначе утечка после размонтирования.
      fit?.detach();
      buttons.clear();
    },
  };
}
